import React, { useEffect, useState } from "react";

/**
 * Two-state reaction bar — heart (like) + thumbs-down (dislike). At most
 * one reaction per wallet per target. Tapping the same icon removes it,
 * tapping the opposite icon switches sides. Optimistic UI: flips
 * immediately, syncs to the server's truth on response.
 *
 * `onToggle(kind)` is async, receives "like" | "dislike", returns
 * { reaction, like_count, dislike_count } from the backend.
 */
const Heart = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24"
       fill={filled ? "currentColor" : "none"}
       stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const ThumbsDown = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24"
       fill={filled ? "currentColor" : "none"}
       stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

export default function LikeButton({
  reaction, likeCount, dislikeCount,
  // legacy aliases so existing call sites that still pass liked/count keep
  // working until they're updated.
  liked, count,
  disabled, onToggle, onDark,
}) {
  // Derive working state from props (server is source of truth).
  const initial = {
    reaction: reaction ?? (liked ? "like" : null),
    like: Number(likeCount ?? (liked ? count : count) ?? 0),
    dislike: Number(dislikeCount ?? 0),
  };
  const [opt, setOpt] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOpt({
      reaction: reaction ?? (liked ? "like" : null),
      like: Number(likeCount ?? (liked ? count : count) ?? 0),
      dislike: Number(dislikeCount ?? 0),
    });
  }, [reaction, likeCount, dislikeCount, liked, count]);

  // Outer fn must be SYNC — React assigns the returned function as the
  // click handler. If outer was async, `click(kind)` would return a Promise
  // and React would never see preventDefault/stopPropagation, causing the
  // click to bubble to the parent <Link> wrapping each thread card.
  const click = (kind) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || disabled) return;
    const prev = opt;
    // Optimistic transition.
    let next = { ...opt };
    if (opt.reaction === kind) {
      // Toggle off.
      next.reaction = null;
      if (kind === "like") next.like = Math.max(0, next.like - 1);
      else next.dislike = Math.max(0, next.dislike - 1);
    } else {
      // Switch sides or set fresh.
      if (opt.reaction === "like") next.like = Math.max(0, next.like - 1);
      if (opt.reaction === "dislike") next.dislike = Math.max(0, next.dislike - 1);
      next.reaction = kind;
      if (kind === "like") next.like += 1;
      else next.dislike += 1;
    }
    setOpt(next);
    setBusy(true);
    try {
      const res = await onToggle(kind);
      if (res && (typeof res.like_count === "number" || typeof res.dislike_count === "number")) {
        setOpt({
          reaction: res.reaction ?? null,
          like: Number(res.like_count ?? next.like),
          dislike: Number(res.dislike_count ?? next.dislike),
        });
      }
    } catch (err) {
      setOpt(prev);
      if (err?.message) alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={`pm-react${onDark ? " on-dark" : ""}`}>
      <button
        type="button"
        className={`pm-react__btn pm-react__btn--like${opt.reaction === "like" ? " is-active" : ""}`}
        onClick={click("like")}
        disabled={busy || disabled}
        aria-pressed={opt.reaction === "like"}
        aria-label={opt.reaction === "like" ? "Unlike" : "Like"}
      >
        <Heart filled={opt.reaction === "like"} />
        <span className="pm-react__count">{opt.like > 0 ? opt.like : ""}</span>
      </button>
      <button
        type="button"
        className={`pm-react__btn pm-react__btn--dislike${opt.reaction === "dislike" ? " is-active" : ""}`}
        onClick={click("dislike")}
        disabled={busy || disabled}
        aria-pressed={opt.reaction === "dislike"}
        aria-label={opt.reaction === "dislike" ? "Remove dislike" : "Dislike"}
      >
        <ThumbsDown filled={opt.reaction === "dislike"} />
        <span className="pm-react__count">{opt.dislike > 0 ? opt.dislike : ""}</span>
      </button>
    </span>
  );
}
